/**
 * Place Grid Card Component
 * Card component for displaying places in grid layout with bookmark icon
 */

import React from 'react';
import {
  typographyColors,
  spacing,
  borders,
  typography,
  fontPrimary,
} from '@/design-system/utils';
import { ActivityCard as ActivityCardType } from '@/constants/chatScreenData';

export interface PlaceGridCardProps {
  card: ActivityCardType;
  onClick?: (cardId: string) => void;
  fixedWidth?: boolean;
}

export const PlaceGridCard: React.FC<PlaceGridCardProps> = ({
  card,
  onClick,
  fixedWidth = false,
}) => {
  return (
    <button
      onClick={() => onClick?.(card.id)}
      style={{
        position: 'relative',
        width: fixedWidth ? '170px' : '100%',
        height: '160px',
        borderRadius: borders.radius.lg,
        overflow: 'hidden',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        justifyContent: 'flex-end',
        isolation: 'isolate', // Create new stacking context for better clipping
        flexShrink: 0, // Prevent shrinking in flex container
      }}
    >
      {/* Background image container with proper clipping */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: borders.radius.lg,
          overflow: 'hidden',
          transform: 'translateZ(0)', // Force GPU acceleration for smoother rendering
          willChange: 'transform',
          backgroundColor: card.image ? 'transparent' : '#F5F5F5', // Empty placeholder background
        }}
      >
        {card.image && (
          <>
            {/* eslint-disable-next-line @next/next/no-img-element -- dynamic card image */}
            <img
              src={card.image}
              alt={card.title}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: '50% 50%',
                pointerEvents: 'none',
                borderRadius: borders.radius.lg,
                transform: 'translateZ(0)', // Force GPU acceleration
                WebkitBackfaceVisibility: 'hidden',
                backfaceVisibility: 'hidden',
              }}
            />
          </>
        )}
      </div>

      {/* Gradient overlay with container */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.3) 55.769%, rgba(0, 0, 0, 0.8) 100%)',
          borderRadius: borders.radius.lg,
          padding: spacing[1], // 4px padding
          display: 'flex',
          alignItems: 'flex-end',
          transform: 'translateZ(0)', // Force GPU acceleration
          WebkitBackfaceVisibility: 'hidden',
          backfaceVisibility: 'hidden',
        }}
      >
        {/* Image Container - holds info */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            justifyContent: 'flex-end',
            width: '100%',
            height: '100%',
          }}
        >
          {/* Info Container with semi-transparent background */}
          <div
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.4)', // Semi-transparent black background (lighter)
              width: '100%',
              border: '1px solid rgba(255, 255, 255, 0.2)', // Light grey border
              borderRadius: borders.radius.md,
              paddingLeft: spacing[3], // 12px
              paddingRight: spacing[2], // 8px
              paddingTop: spacing[1], // 4px
              paddingBottom: spacing[1], // 4px
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: spacing[1.5], // 6px
            }}
          >
            {/* Location name */}
            <p
              style={{
                flex: '1 1 0',
                fontFamily: fontPrimary,
                fontSize: '12px',
                fontWeight: typography.weight.semibold,
                color: typographyColors.alternate,
                lineHeight: 'normal',
                margin: 0,
                textAlign: 'left',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0, // Allow shrinking
              }}
            >
              {card.title}
            </p>

            {/* Arrow Icon */}
            <svg
              width='20'
              height='20'
              viewBox='0 0 20 20'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
              style={{
                flexShrink: 0,
              }}
            >
              <path
                d='M5 15L15 5M15 5H5M15 5V15'
                stroke='white'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        </div>
      </div>
    </button>
  );
};
